import { useEffect, useId, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { TabsContent } from "@/components/ui/tabs";
import { Search } from "lucide-react";
import statisticsAPIService, { type CombinationCatalogEntry } from "@/lib/statistics-api.service";

/** Chờ người dùng ngừng gõ trước khi gọi API — mỗi phím một request là vô ích. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * Tab "Tra tổ hợp": tra bảng mã tổ hợp xét tuyển chuẩn của Bộ bằng từ khoá tự
 * do — gõ mã ("a00"), tên môn ("hoá", "tin"), hay nhiều môn một lúc
 * ("toan ly tin").
 *
 * Khác mọi tab còn lại: đây là DỮ LIỆU QUY CHẾ, không phải số liệu thí sinh —
 * không chịu bộ lọc khoa/khoá/ngày nào của trang, và không đổi khi dữ liệu
 * tuyển sinh đổi.
 *
 * Việc so khớp (bỏ dấu, mọi từ khoá đều phải khớp, ưu tiên khớp mã) làm ở
 * server chứ không phải ở đây: một quy tắc "thế nào là khớp" nằm ở hai phía sẽ
 * có ngày lệch nhau.
 */
export default function CombinationLookupTab() {
	const [query, setQuery] = useState("");
	const [entries, setEntries] = useState<CombinationCatalogEntry[] | undefined>(undefined);
	const [error, setError] = useState<string | null>(null);
	const searchId = `${useId()}-combo-search`;

	useEffect(() => {
		let cancelled = false;
		const timer = setTimeout(() => {
			statisticsAPIService
				.getCombinationCatalog(query)
				.then((data) => {
					if (cancelled) return;
					setEntries(data);
					setError(null);
				})
				.catch((err) => {
					if (cancelled) return;
					console.error("Error fetching combination catalog:", err);
					setError(err instanceof Error ? err.message : "Không tra được bảng mã tổ hợp");
				});
		}, SEARCH_DEBOUNCE_MS);

		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [query]);

	return (
		<TabsContent value="combinations">
			<div className="space-y-4">
				<Card>
					<CardContent className="p-3">
						<div className="flex flex-col gap-2 sm:flex-row sm:items-center">
							<label
								htmlFor={searchId}
								className="text-xs font-medium text-muted-foreground whitespace-nowrap"
							>
								Tra tổ hợp:
							</label>
							<div className="relative w-full sm:max-w-lg">
								<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
								<Input
									id={searchId}
									value={query}
									onChange={(e) => setQuery(e.target.value)}
									placeholder="Gõ mã (A00, X06) hoặc tên môn (hoá, tin, toan ly tin)..."
									className="h-9 pl-9"
								/>
							</div>
						</div>
						<p className="mt-2 text-xs text-muted-foreground">
							Gõ không dấu cũng được. Nhiều từ khoá = tổ hợp phải có đủ các môn đó.
						</p>
					</CardContent>
				</Card>

				{error && (
					<p className="text-sm text-destructive" role="alert">
						{error}
					</p>
				)}

				<Card>
					<CardContent className="p-4 md:p-6">
						{entries === undefined ? (
							<div className="space-y-2">
								{Array.from({ length: 8 }).map((_, i) => (
									<Skeleton key={i} className="h-8 w-full" />
								))}
							</div>
						) : entries.length === 0 ? (
							<p className="py-8 text-center text-sm text-muted-foreground">
								Không có tổ hợp nào khớp "{query}".
							</p>
						) : (
							<>
								<p className="mb-3 text-sm text-muted-foreground">
									{entries.length.toLocaleString("vi-VN")} tổ hợp
								</p>
								{/* Hai cột trên màn hình rộng: bảng có gần trăm dòng ngắn, một
								    cột dọc bắt cuộn dài gấp đôi mà bỏ trống nửa bề ngang. */}
								<div className="grid grid-cols-1 gap-x-8 lg:grid-cols-2">
									{entries.map((entry) => (
										<div key={entry.code} className="flex items-baseline gap-3 border-b py-2">
											<span className="w-12 flex-shrink-0 font-semibold tabular-nums">
												{entry.code}
											</span>
											<span className="min-w-0 flex-1 text-sm">{entry.subjects.join("; ")}</span>
										</div>
									))}
								</div>
							</>
						)}
					</CardContent>
				</Card>

				<p className="text-xs text-muted-foreground">
					Nguồn: bảng mã tổ hợp xét tuyển năm 2025 của Hệ thống tuyển sinh chung Bộ GD&amp;ĐT. Đây
					là dữ liệu quy chế — không chịu bộ lọc khoa/khoá của trang và không đổi theo dữ liệu tuyển
					sinh.
				</p>
			</div>
		</TabsContent>
	);
}
